/* eslint-disable max-lines */
import { useLocations } from '@hakimo-ui/hakimo/data-access';
import { LocationFilters } from '@hakimo-ui/hakimo/types';
import { Select } from '@hakimo-ui/hakimo/ui-elements';
import { Column, ColumnsMenu } from '@hakimo-ui/hakimo/ui-table';
import {
  eventTracker,
  useExportCsv,
  useIsSupportRole,
} from '@hakimo-ui/hakimo/util';
import { Button, InputField } from '@hakimo-ui/shared/ui-base';
import { ArrowDownTrayIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import {
  FilterItem,
  getFormattedExportData,
  getSearchParams,
  searchFilters,
} from './util';

interface Props {
  hasCustomFiltersApplied: boolean;
  isLoading: boolean;
  onClickFilter: () => void;
  onSearch: (value: string) => void;
  filterValue: FilterItem;
  onChangeFilter: (value: FilterItem) => void;
  allColumns: Column[];
  shownColumns: string[];
  onChangeShownColumns: (val: string[]) => void;
  filters: LocationFilters;
  isDataValid: boolean;
  searchValue: string;
}

export function TableHeader(props: Props) {
  const {
    hasCustomFiltersApplied,
    isLoading,
    onClickFilter,
    onSearch,
    filterValue,
    onChangeFilter,
    allColumns,
    shownColumns,
    onChangeShownColumns,
    filters,
    isDataValid,
    searchValue,
  } = props;

  const [isExporting, setIsExporting] = useState(false);

  const exportSearchParams = getSearchParams(filters);
  const { refetch: fetchExportData } = useLocations(exportSearchParams, false);

  const { exportCsv } = useExportCsv();
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { data } = await fetchExportData();
      if (!data?.items) {
        throw new Error('Data Fetching Failed');
      }

      const formattedData = getFormattedExportData(data.items);
      if (!Array.isArray(formattedData) || formattedData.length === 0) {
        throw new Error('Formatted data is invalid or empty');
      }
      exportCsv({
        formattedData,
        fileName: 'hakimo-locations-export.csv',
        fileType: 'csv',
      });
    } catch (error) {
      throw new Error(
        `Export Failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setIsExporting(false);
    }
  };

  const isSupportRole = useIsSupportRole();
  const disableExport = !isDataValid || !isSupportRole;

  const displayValue = (item?: FilterItem) => item?.name || '';

  const onSearchValChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onSearch(e.target.value);
  const onChange = (value: FilterItem | null) => {
    value && onChangeFilter(value);
  };

  const allColumnsWithoutActions = useMemo(
    () => allColumns.filter((col) => col.id !== 'actions'),
    [allColumns]
  );

  return (
    <div className="dark:bg-dark-bg flex items-center justify-end border-b bg-white p-4 dark:border-0">
      <div className="flex flex-row items-center justify-end gap-4">
        <div className="bg-onlight-bg-2 dark:bg-ondark-bg-2 flex flex-row items-center justify-end gap-2 rounded-md p-1">
          <InputField
            label=""
            onChange={onSearchValChange}
            value={searchValue}
            type="search"
            placeholder={`Filter with ${filterValue.name}...`}
          />
          <div className="w-60">
            <Select
              value={filterValue}
              getItems={() => searchFilters}
              displayValue={displayValue}
              onChange={onChange}
            />
          </div>
        </div>
        <ColumnsMenu
          options={allColumnsWithoutActions}
          shownColumns={shownColumns}
          onChangeShownColumns={onChangeShownColumns}
        />
        <Button
          variant="icon"
          onClick={handleExport}
          loading={isExporting}
          title="Download Data"
          disabled={disableExport}
        >
          <ArrowDownTrayIcon className="w-5" />
        </Button>
        <Button
          variant="icon"
          badge={hasCustomFiltersApplied}
          onClick={onClickFilter}
          disabled={isLoading}
          onSideEffect={eventTracker('open_location_filters_panel')}
        >
          <span className="sr-only">Open filters panel</span>
          <FunnelIcon className="h-5 w-5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

export default TableHeader;
